import { SessionStorageKeys } from './session-storage-keys';

export const JourneyStages = {
	Visit: 'Visit',
	CreateOrganization: 'CreateOrganization',
	CreateForm: 'CreateForm',
	ActivateForm: 'ActivateForm',
	ConvertToPaid: 'ConvertToPaid'
} as const;

export type JourneyStage = typeof JourneyStages[keyof typeof JourneyStages];

export const STAGE_HEADER = 'X-CF-Stage';

export const RETIRED_STAGE_HEADER = 'X-CF-Stage-Retired';

export const DEFERRED_CANCEL_COOKIE = 'c-journey-cancel';

export type ActiveStage = {
	stage: JourneyStage;
	variant: string;
	stageCorrelationId: string;
	startedAt: number;
	entryPoint?: string;
	detail?: string;
};

export type StageStart = {
	stage: JourneyStage;
	variant: string;
	entryPoint?: string;
	detail?: string;
	stageCorrelationId?: string;
};

type StageMap = Partial<Record<JourneyStage, ActiveStage>>;

let fallbackStore: string | null = null;

export function isJourneyStage(value: string): value is JourneyStage {
	return Object.prototype.hasOwnProperty.call(JourneyStages, value);
}

export const MAX_TOKEN_LENGTH = 128;

/**
 * Reduces a value to what can safely ride in the `X-CF-Stage` header: `A-Z a-z 0-9 _ -`, truncated at
 * `maxLength`. Anything outside that alphabet is dropped rather than escaped, so no dimension can reparse
 * into another header field. Must stay identical to `JourneyContext.Token` on the server.
 */
export function journeyToken(value: string | undefined, maxLength: number = MAX_TOKEN_LENGTH): string {
	if (!value)
		return '';
	return value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, maxLength);
}

function createStageCorrelationId(): string {
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function readRaw(): string | null {
	try {
		return sessionStorage.getItem(SessionStorageKeys.UserJourney);
	}
	catch (e) {
		return fallbackStore;
	}
}

function writeRaw(value: string): void {
	fallbackStore = value;
	try {
		sessionStorage.setItem(SessionStorageKeys.UserJourney, value);
	}
	catch (e) {
		// storage is unavailable; the in-memory copy keeps the stage alive for this document
	}
}

function isActiveStage(value: unknown): value is ActiveStage {
	if (!value || typeof value !== 'object')
		return false;
	const candidate = value as Record<string, unknown>;
	return typeof candidate.stage === 'string' && isJourneyStage(candidate.stage)
		&& typeof candidate.variant === 'string'
		&& typeof candidate.stageCorrelationId === 'string'
		&& typeof candidate.startedAt === 'number';
}

function readStages(): StageMap {
	const raw = readRaw();
	if (!raw)
		return {};

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	}
	catch (e) {
		return {};
	}

	if (!parsed || typeof parsed !== 'object')
		return {};

	const stages: StageMap = {};
	for (const [stage, value] of Object.entries(parsed as Record<string, unknown>)) {
		if (isJourneyStage(stage) && isActiveStage(value))
			stages[stage] = value;
	}
	return stages;
}

function writeStages(stages: StageMap): void {
	writeRaw(JSON.stringify(stages));
}

/**
 * Opens a stage and mints the correlation id its Start, Steps and Complete will share, replacing any stage
 * already open under the same name.
 *
 * Pass `stageCorrelationId` for a stage that completes somewhere this tab's header cannot reach, so both
 * halves can key off a subject that already exists. Several Starts then share one id.
 */
export function beginStage(start: StageStart, now: number = Date.now()): ActiveStage {
	const active: ActiveStage = {
		stage: start.stage,
		variant: start.variant,
		stageCorrelationId: start.stageCorrelationId ?? createStageCorrelationId(),
		startedAt: now
	};
	if (start.entryPoint)
		active.entryPoint = start.entryPoint;
	if (start.detail)
		active.detail = start.detail;

	const stages = readStages();
	stages[start.stage] = active;
	writeStages(stages);
	return active;
}

/**
 * The stage a step or completion belongs to — the named one when given, otherwise the most recently
 * started.
 */
export function currentStage(stage?: JourneyStage): ActiveStage | null {
	const stages = readStages();
	if (stage)
		return stages[stage] ?? null;

	let latest: ActiveStage | null = null;
	for (const active of Object.values(stages)) {
		if (active && (!latest || active.startedAt > latest.startedAt))
			latest = active;
	}
	return latest;
}

export function openStages(): ActiveStage[] {
	return Object.values(readStages()).filter(Boolean) as ActiveStage[];
}

export function clearStage(stage: JourneyStage): void {
	const stages = readStages();
	delete stages[stage];
	writeStages(stages);
}

export function clearAllStages(): void {
	writeStages({});
}

/**
 * Stops advertising the stages the server has told us are over, named as a comma-separated list by the
 * {@link RETIRED_STAGE_HEADER} response header. Nothing else in the tab expires a stage, so one left open
 * rides `X-CF-Stage` for the rest of the session.
 */
export function retireStages(headerValue: string | null): void {
	if (!headerValue)
		return;

	const stages = readStages();
	let changed = false;
	for (const name of headerValue.split(',')) {
		if (isJourneyStage(name) && stages[name]) {
			delete stages[name];
			changed = true;
		}
	}

	if (changed)
		writeStages(stages);
}

function describeStage(active: ActiveStage): string {
	let described = `${active.stage};v=${journeyToken(active.variant)};c=${journeyToken(active.stageCorrelationId)};t=${active.startedAt}`;
	const dim1 = journeyToken(active.entryPoint);
	const dim2 = journeyToken(active.detail);
	if (dim1)
		described += `;d1=${dim1}`;
	if (dim2)
		described += `;d2=${dim2}`;
	return described;
}

/**
 * The `X-CF-Stage` request header value, or null when no stage is open. Every open stage rides, comma
 * separated and oldest first, so a request that commits two of them has both to look up by name. The `,`
 * cannot occur inside a stage's own description — every field is either a closed set, digits, or put
 * through {@link journeyToken}.
 */
export function journeyStageHeader(): string | null {
	const open = openStages().sort((a, b) => a.startedAt - b.startedAt);
	if (!open.length)
		return null;

	return open.map(describeStage).join(',');
}
